import { useCallback, useMemo, useState } from "react";
import type { InvoiceRow } from "@/types/api";

const defaultIsSelectable = (row: InvoiceRow) => row.exportable !== false;

export function useInvoiceSelection(
  items: InvoiceRow[],
  isSelectable: (row: InvoiceRow) => boolean = defaultIsSelectable,
) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const exportableIds = useMemo(
    () => items.filter(isSelectable).map((r) => r.id),
    [items, isSelectable],
  );

  const selectedOnPage = useMemo(
    () => exportableIds.filter((id) => selected.has(id)).length,
    [exportableIds, selected],
  );

  const allOnPageSelected =
    exportableIds.length > 0 && selectedOnPage === exportableIds.length;

  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllOnPage = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        for (const id of exportableIds) next.delete(id);
      } else {
        for (const id of exportableIds) next.add(id);
      }
      return next;
    });
  }, [allOnPageSelected, exportableIds]);

  const clear = useCallback(() => setSelected(new Set()), []);

  const setSelectedIds = useCallback((ids: string[]) => {
    setSelected(new Set(ids));
  }, []);

  const selectedIds = useMemo(() => [...selected], [selected]);

  return {
    selected,
    selectedIds,
    selectedCount: selected.size,
    selectedOnPage,
    allOnPageSelected,
    someOnPageSelected,
    toggle,
    toggleAllOnPage,
    clear,
    setSelectedIds,
  };
}
